import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowDown, ArrowRight, Download, Shield, MessageCircle } from "lucide-react";
import { BACKEND_URL, api } from "../lib/api";
import {
  RENA, useRenalifePage, useRenalifeCta, Eyebrow, PageShell, LoadingScreen, NotFoundScreen, VideoBlock, Footer,
} from "./RenalifeLanding";

const RANKS = [
  { id: 1, stars: "1★", directs: "2-3", rate: 200 },
  { id: 2, stars: "2★", directs: "4-5", rate: 400 },
  { id: 3, stars: "3★", directs: "6-7", rate: 600 },
  { id: 4, stars: "4★", directs: "8-9", rate: 800 },
  { id: 5, stars: "5★", directs: "10+", rate: 1000 },
];

const mxn = (n) => `$${n.toLocaleString("es-MX")} MXN`;

const DiffExample = ({ mine, buyer, level }) => {
  const m = RANKS[mine - 1];
  const b = RANKS[buyer - 1];
  const diff = Math.max(0, m.rate - b.rate);
  return (
    <div className="rounded-2xl border p-5" style={{ borderColor: diff > 0 ? `${RENA.gold}40` : "#ffffff20", background: "#03251c" }}>
      <div className="font-mono text-[11px] uppercase tracking-widest mb-3" style={{ color: "#94a3b8" }}>Compra en tu Nivel {level}</div>
      <div className="text-sm" style={{ color: "#cbd5d1" }}>
        Tú ({m.stars}, ${m.rate}) − Comprador ({b.stars}, ${b.rate})
      </div>
      <div className="font-renalife text-2xl mt-2" style={{ color: diff > 0 ? RENA.gold : "#94a3b8" }}>
        = {mxn(diff)} {diff > 0 ? "para ti" : "en esa línea"}
      </div>
    </div>
  );
};

const Simulator = () => {
  const [rank, setRank] = useState(3);
  const [buyerRank, setBuyerRank] = useState(1);
  const [n1, setN1] = useState(10);
  const [deep, setDeep] = useState(40);
  const myRate = RANKS[rank - 1].rate;
  const diff = Math.max(0, myRate - RANKS[buyerRank - 1].rate);
  const n1Total = myRate * n1;
  const deepTotal = diff * deep;

  const Pill = ({ r, active, onClick, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className="rounded-full px-4 py-2 text-sm font-bold transition-colors"
      style={active ? { background: RENA.gold, color: RENA.deep } : { background: "#064E3B", color: "#cbd5d1", border: "1px solid #ffffff20" }}
    >
      {r.stars}
    </button>
  );

  return (
    <div data-testid="infinito-simulator" className="rounded-3xl border p-7 sm:p-10 mt-10" style={{ borderColor: `${RENA.gold}30`, background: "#03251c" }}>
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="space-y-8">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: RENA.gold }}>1 · Tu rango</div>
            <div className="flex flex-wrap gap-2">
              {RANKS.map((r) => <Pill key={r.id} r={r} active={rank === r.id} onClick={() => setRank(r.id)} testid={`sim-rank-${r.id}`} />)}
            </div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-widest mb-2" style={{ color: RENA.gold }}>2 · Compras recurrentes en tu Nivel 1 (protegido)</div>
            <input data-testid="sim-n1" type="range" min={0} max={30} value={n1} onChange={(e) => setN1(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{ accentColor: RENA.gold, background: `${RENA.gold}30` }} />
            <div className="font-mono text-sm mt-1" style={{ color: "#cbd5d1" }}>{n1} compras × ${myRate}</div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-widest mb-2" style={{ color: RENA.gold }}>3 · Compras en tus Niveles 2 al 20</div>
            <input data-testid="sim-deep" type="range" min={0} max={100} value={deep} onChange={(e) => setDeep(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{ accentColor: RENA.gold, background: `${RENA.gold}30` }} />
            <div className="font-mono text-sm mt-1" style={{ color: "#cbd5d1" }}>{deep} compras</div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: RENA.gold }}>4 · Rango promedio de esos compradores</div>
            <div className="flex flex-wrap gap-2">
              {RANKS.map((r) => <Pill key={r.id} r={r} active={buyerRank === r.id} onClick={() => setBuyerRank(r.id)} testid={`sim-buyer-${r.id}`} />)}
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-center rounded-2xl border p-7" style={{ borderColor: "#ffffff15", background: RENA.deep }}>
          <div className="flex justify-between text-sm py-2 border-b" style={{ borderColor: "#ffffff12", color: "#cbd5d1" }}>
            <span>Nivel 1 protegido ({n1} × ${myRate})</span><span className="font-mono">{mxn(n1Total)}</span>
          </div>
          <div className="flex justify-between text-sm py-2 border-b" style={{ borderColor: "#ffffff12", color: "#cbd5d1" }}>
            <span>Niveles 2-20 ({deep} × ${diff} diferencial)</span><span className="font-mono">{mxn(deepTotal)}</span>
          </div>
          <div className="mt-6">
            <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: "#94a3b8" }}>Estimado del corte mensual</div>
            <div data-testid="sim-total" className="font-renalife text-4xl sm:text-5xl mt-1" style={{ color: RENA.gold }}>{mxn(n1Total + deepTotal)}</div>
          </div>
          {diff === 0 && (
            <p className="text-xs mt-4" style={{ color: "#94a3b8" }}>
              Con compradores de tu mismo rango o superior, el diferencial es $0 en esa línea — sube de rango para abrir la brecha.
            </p>
          )}
        </div>
      </div>
      <p className="text-xs mt-8" style={{ color: "#64748b" }}>
        Simulador educativo. Los ingresos reales dependen del comportamiento de compra recurrente de tu red y del
        cumplimiento del plan de compensación publicado.
      </p>
    </div>
  );
};

export default function RenalifeInfinito() {
  const { slug } = useParams();
  const { data, notFound } = useRenalifePage(slug);
  const talk = useRenalifeCta(slug, "bono3_talk");

  const downloadPlan = () => {
    api.post(`/public/renalife/${slug}/interest`, { name: "", bono: "bono3" }).catch(() => {});
    window.open(`${BACKEND_URL}/api/public/renalife-pdf/plan_compensacion`, "_blank");
  };

  if (notFound) return <NotFoundScreen />;
  if (!data) return <LoadingScreen />;

  const img = data.assets?.bono3_infinito?.image_url;
  const video = data.assets?.bono3_infinito?.video_url;

  return (
    <PageShell testid="renalife-landing-bono3">
      {/* 1) HERO minimalista */}
      <section className="min-h-screen flex items-center px-6 sm:px-12" style={{ background: `linear-gradient(160deg, ${RENA.deep} 0%, ${RENA.forest} 130%)` }}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[3fr_2fr] gap-12 items-center py-16 w-full">
          <div>
            <Eyebrow>BONO 003 · EL MOTOR DEL INGRESO RESIDUAL</Eyebrow>
            <h1 className="font-renalife font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.1] mt-6">
              20 niveles.<br />Infinitas compras.<br />Un solo cálculo <em className="italic font-medium" style={{ color: RENA.gold }}>justo</em>.
            </h1>
            <p className="text-base sm:text-lg mt-7 max-w-xl" style={{ color: "#cbd5d1" }}>
              El Bono Infinito con Diferenciales paga cada compra recurrente de $3,200 MXN dentro de tu red,
              hasta 20 niveles de profundidad — con una regla matemática que premia el crecimiento vertical
              de tu rango, no la suerte.
            </p>
            <a data-testid="cta-math" href="#tarifas" className="inline-flex items-center gap-2 text-sm font-semibold mt-9 hover:opacity-80" style={{ color: RENA.gold }}>
              Ver la matemática <ArrowDown size={15} />
            </a>
          </div>
          {img && (
            <img data-testid="hero-image-bono3" src={`${BACKEND_URL}${img}`} alt="Bono Infinito" className="rounded-3xl border shadow-2xl w-full object-cover max-h-[560px]" style={{ borderColor: `${RENA.gold}25` }} />
          )}
        </div>
      </section>

      {/* 2) TABLA DE TARIFAS */}
      <section id="tarifas" className="max-w-3xl mx-auto px-6 sm:px-12 py-20">
        <Eyebrow>La tabla de tarifas</Eyebrow>
        <h2 className="font-renalife text-2xl sm:text-3xl mt-4 mb-8">Tu rango define cuánto vale cada compra.</h2>
        <div data-testid="rates-table" className="rounded-2xl border overflow-hidden" style={{ borderColor: `${RENA.gold}30` }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="font-mono text-[11px] uppercase tracking-widest" style={{ background: "#03251c", color: RENA.gold }}>
                <th className="text-left px-5 py-3.5">Rango</th>
                <th className="text-left px-5 py-3.5">Directos activos</th>
                <th className="text-right px-5 py-3.5">Tarifa por compra</th>
              </tr>
            </thead>
            <tbody>
              {RANKS.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 ? "#03251c60" : "transparent", borderTop: "1px solid #ffffff10" }}>
                  <td className="px-5 py-3.5 font-renalife text-lg" style={{ color: RENA.gold }}>{r.stars}</td>
                  <td className="px-5 py-3.5" style={{ color: "#cbd5d1" }}>{r.directs}</td>
                  <td className="px-5 py-3.5 text-right font-mono" style={{ color: "#F8FAF8" }}>${r.rate.toLocaleString("es-MX")} MXN{r.id === 5 ? " (tope)" : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs mt-3" style={{ color: "#64748b" }}>Tu rango se recalcula cada corte mensual.</p>
      </section>

      {/* 3) N1 PROTEGIDO */}
      <section className="px-6 sm:px-12 py-20" style={{ background: "#03251c" }}>
        <div className="max-w-3xl mx-auto text-center">
          <Eyebrow>Concepto clave</Eyebrow>
          <div data-testid="n1-shield" className="relative w-24 h-24 mx-auto mt-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center font-bold text-lg mx-auto" style={{ background: "#064E3B", border: `1px solid ${RENA.gold}60`, color: "#F8FAF8" }}>N1</div>
            <Shield className="absolute -top-2 -right-1" size={38} fill={RENA.gold} style={{ color: RENA.gold }} />
          </div>
          <h2 className="font-renalife text-2xl sm:text-3xl mt-6">N1 protegido</h2>
          <p className="text-base sm:text-lg mt-4 max-w-xl mx-auto" style={{ color: "#cbd5d1" }}>
            En tu Nivel 1 SIEMPRE cobras tu tarifa completa, sin importar el rango del comprador.
            Es tu <em className="italic" style={{ color: RENA.gold }}>"renta base"</em>.
          </p>
        </div>
      </section>

      {/* 4) DEL N2 AL N20 — EL DIFERENCIAL */}
      <section className="max-w-5xl mx-auto px-6 sm:px-12 py-20">
        <Eyebrow>Del N2 al N20</Eyebrow>
        <h2 className="font-renalife text-2xl sm:text-3xl mt-4 mb-8">El diferencial: la brecha entre tu rango y el de quien compra.</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <DiffExample mine={3} buyer={2} level={5} />
          <DiffExample mine={5} buyer={1} level={8} />
          <DiffExample mine={4} buyer={3} level={12} />
          <DiffExample mine={2} buyer={2} level={3} />
        </div>
        <p className="text-sm sm:text-base mt-8 max-w-2xl" style={{ color: "#a7bab3" }}>
          Nunca cobras menos que 0. Si el comprador está en tu mismo rango o superior, el diferencial es 0 en esa
          línea — pero cobrarás en la siguiente compra o desde el siguiente nivel.
        </p>
      </section>

      {/* 5) SIMULADOR */}
      <section className="px-6 sm:px-12 py-20" style={{ background: "#022a20" }}>
        <div className="max-w-5xl mx-auto">
          <Eyebrow>Ejemplo real</Eyebrow>
          <h2 className="font-renalife text-2xl sm:text-3xl mt-4">Simula tu corte mensual.</h2>
          <Simulator />
        </div>
      </section>

      <VideoBlock videoUrl={video} title="El Bono Infinito en 30 segundos" />

      {/* 6) CIERRE + CTA */}
      <section className="max-w-4xl mx-auto px-6 sm:px-12 py-20 text-center">
        <p className="font-renalife text-2xl sm:text-4xl leading-snug">
          Este no es un bono para prisas.<br />
          Es el bono que sostendrá tus próximos <span style={{ color: RENA.gold }}>5 años</span> en Renalife.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <button
            data-testid="cta-pdf"
            onClick={downloadPlan}
            className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-base transition-transform hover:-translate-y-0.5"
            style={{ background: RENA.gold, color: RENA.deep }}
          >
            <Download size={17} /> Descarga el PDF oficial del plan de compensación
          </button>
          <button
            data-testid="cta-sponsor"
            onClick={talk.go}
            disabled={talk.sending}
            className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-semibold text-base border transition-colors hover:bg-white/5"
            style={{ borderColor: `${RENA.gold}60`, color: RENA.gold }}
          >
            <MessageCircle size={17} /> Habla con tu patrocinador <ArrowRight size={15} />
          </button>
        </div>
      </section>
      <Footer name={data.distributor_name} />
    </PageShell>
  );
}
